/**
 * src/App.jsx
 * Engine tab now embedded in TenantManager — no separate /engine route needed.
 */

import { useCallback, useState, useEffect, useRef, Suspense } from 'react';
// NOT React.lazy. `lazyWithTimeout` has the same signature and caching
// semantics, but rejects a chunk fetch that never settles instead of leaving
// <Suspense> on its near-empty fallback forever — see src/utils/lazyWithTimeout.js
// for why a stalled chunk is a real, observed failure mode here and not a
// hypothetical one. Aliased to `lazy` so the ~45 call sites below stay as they
// were; the behaviour change is declared once, here.
import { lazyWithTimeout as lazy } from './utils/lazyWithTimeout.js';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';

import { useTenant } from './tenant/useTenant.js';
import GrcAuditorApp from './grc/GrcAuditorApp.jsx';
import ReleaseFlagGate from './components/shared/ReleaseFlagGate.jsx';
import { useAuth } from './state/useAuth.js';
import { ControlFrameworkPreferenceProvider } from './state/ControlFrameworkPreferenceContext.jsx';
import { useAppState } from './state/useAppState.js';
import { useModalStack } from './state/useModalStack.js';
import { ModalStackDepthContext } from './state/ModalStackDepthContext.js';
import { useEngine } from './engine/useEngine.js';
import { useTheme } from './state/useTheme.js';
import { usePlanGating } from './hooks/usePlanGating.js';
import { useReleaseFlag } from './utils/useReleaseFlag.js';

import {
  canEdit,
  canAdmin,
  isProspect,
  getModulePermissions,
  EXEC_DASHBOARD_MODULE,
  resolveSeasuiteRole,
} from './utils/auth.js';
import { readAppVersion, readUiLayout } from './api/storage-azure.js';
import { setSessionToken, azurePost } from './api/azureClient.js';
import { DEFAULT_LAYOUT_ID, getLayout, getVersionForLayout } from './layouts/registry.js';
import { configureDiagnostics } from './diagnostics/diagnosticsClient.js';

import Sidebar from './components/layout/Sidebar.jsx';
import TopBar from './layouts/aurora/TopBar.jsx';
import AuroraSidebar from './layouts/aurora/AuroraSidebar.jsx';
import { AURORA_DARK_VARS, AURORA_LIGHT_VARS } from './layouts/aurora/palettes.js';
import EnvironmentBanner from './components/layout/EnvironmentBanner.jsx';
import SaveConflictBanner from './components/layout/SaveConflictBanner.jsx';
import ServiceRestartBanner from './components/layout/ServiceRestartBanner.jsx';
import WriteErrorBanner from './components/layout/WriteErrorBanner.jsx';
import StaleStateBanner from './components/layout/StaleStateBanner.jsx';
import ApiErrorBanner from './components/layout/ApiErrorBanner.jsx';
import StepUpDialog from './components/auth/StepUpDialog.jsx';
import { superAdminStepUpApi } from './components/superadmin/superAdminStepUpApi.jsx';
import TopBannerStack from './components/layout/TopBannerStack.jsx';
// Route-level page components — lazy-loaded so they are excluded from the
// initial JS bundle and only fetched when the user navigates to that route.
const Dashboard = lazy(() => import('./components/dashboard/Dashboard.jsx'));
const NextStepsPage = lazy(() => import('./components/nextsteps/NextStepsPage.jsx'));
const GuidesPage = lazy(() => import('./components/guides/GuidesPage.jsx'));
const ObligationCoverageGuide = lazy(
  () => import('./components/guides/ObligationCoverageGuide.jsx')
);
const Documents = lazy(() => import('./components/documents/Documents.jsx'));
const Tasks = lazy(() => import('./components/tasks/Tasks.jsx'));
const ControlsModule = lazy(() => import('./components/controls/ControlsModule.jsx'));
const FrameworkManager = lazy(() => import('./components/frameworks/FrameworkManager.jsx'));
const Risks = lazy(() => import('./components/risks/Risks.jsx'));
const AIGovernancePage = lazy(() => import('./components/aiGovernance/AIGovernancePage.jsx'));
const ThreatsPage = lazy(() => import('./components/threats/ThreatsPage.jsx'));
const VulnerabilitiesPage = lazy(
  () => import('./components/vulnerabilities/VulnerabilitiesPage.jsx')
);
const PriorityView = lazy(() => import('./components/priority/PriorityView.jsx'));
const GapAnalysisPage = lazy(() => import('./components/gap/GapAnalysisPage.jsx'));
const GrcClientPortal = lazy(() => import('./grc/GrcClientPortal.jsx'));
const FindingsPage = lazy(() => import('./components/findings/FindingsPage.jsx'));
const ComplianceCalendar = lazy(() => import('./components/calendar/ComplianceCalendar.jsx'));
const ActivityLog = lazy(() => import('./components/activitylog/ActivityLog.jsx'));
const SecurityEvents = lazy(() => import('./components/activitylog/SecurityEvents.jsx'));
const MetricExplorerPage = lazy(() => import('./components/improvement/MetricExplorerPage.jsx'));
const RecurrenceReviewPage = lazy(
  () => import('./components/improvement/RecurrenceReviewPage.jsx')
);
const ContextChangeRegisterPage = lazy(
  () => import('./components/improvement/ContextChangeRegisterPage.jsx')
);
const ObjectivesPage = lazy(() => import('./components/improvement/ObjectivesPage.jsx'));
const WorkflowsPage = lazy(() => import('./components/workflows/WorkflowsPage.jsx'));
const ImprovementPage = lazy(() => import('./components/improvement/ImprovementPage.jsx'));
const EmailBreachMonitor = lazy(() => import('./components/security/EmailBreachMonitor.jsx'));
const Settings = lazy(() => import('./components/settings/Settings.jsx'));
const UserManagement = lazy(() => import('./components/users/UserManagement.jsx'));

import SplashScreen from './components/shared/SplashScreen.jsx';
import LoginScreen from './components/auth/LoginScreen.jsx';
import SsoCallback from './components/auth/SsoCallback.jsx';
import UniversalLoginPage from './components/auth/UniversalLoginPage.jsx';
import NoTenant from './components/auth/NoTenant.jsx';
import SuspendedTenant from './components/auth/SuspendedTenant.jsx';
import EmployeeMagicLinkRequest from './components/auth/EmployeeMagicLinkRequest.jsx';
import EmployeeMagicLinkVerify from './components/auth/EmployeeMagicLinkVerify.jsx';
import CandidateMagicLinkVerify from './components/candidates/CandidateMagicLinkVerify.jsx';
import AccessApprovalBanner from './components/superadmin/AccessApprovalBanner.jsx';
import PaymentWarningBanner from './components/billing/PaymentWarningBanner.jsx';
import GlobalRegulatoryAlertBanner from './components/incidents/GlobalRegulatoryAlertBanner.jsx';

// Vendor module — lazy (large module, plan-gated)
const VendorsPage = lazy(() => import('./components/vendors/VendorsPage.jsx'));
const VendorAssessmentPortal = lazy(
  () => import('./components/vendors/VendorAssessmentPortal.jsx')
);
const SupplierPortal = lazy(() => import('./components/supplier/SupplierPortal.jsx'));
// Employee module — lazy (largest feature module, ~148 KB)
const EmployeesPage = lazy(() => import('./components/employees/EmployeesPage.jsx'));
const EmployeeHub = lazy(() => import('./components/employees/EmployeeHub.jsx'));
const CandidatesPage = lazy(() => import('./components/candidates/CandidatesPage.jsx'));
const CandidateHub = lazy(() => import('./components/candidates/CandidateHub.jsx'));

// BIA — lazy (plan-gated)
const BIAPage = lazy(() => import('./components/bia/BIAPage.jsx'));

// BCDR — lazy (plan-gated; BCDRRunbookModal is 136 KB alone)
const BCDRPage = lazy(() => import('./components/bcdr/BCDRPage.jsx'));

// Incidents — lazy (plan-gated)
const IncidentsPage = lazy(() => import('./components/incidents/IncidentsPage.jsx'));

// Management Review — lazy
const ManagementReviewPage = lazy(
  () => import('./components/managementreview/ManagementReviewPage.jsx')
);

// Leader dialogue — the ISMS admin's invite screen (under Management Review)
// and the leader's chrome-less link route. Both lazy.
const LeaderDialoguePage = lazy(() => import('./components/leaderDialogue/LeaderDialoguePage.jsx'));
const LeaderDialogueRoute = lazy(
  () => import('./components/leaderDialogue/LeaderDialogueRoute.jsx')
);

// Assets & Products — lazy
const AssetsPage = lazy(() => import('./components/assets/AssetsPage.jsx'));
const ProductsPage = lazy(() => import('./products/ProductsPage.jsx'));

// Supplier chain: Customers → Delivery Agreements → Subcontractors — one
// tabbed page, lazy (plan-gated, SKI-0215). Each step keeps its own route.
const SupplierChainHub = lazy(() => import('./components/supplier/SupplierChainHub.jsx'));
const AgreementsPage = lazy(() => import('./components/supplier/AgreementsPage.jsx'));

// Privacy hub — lazy (six sub-tabs, heavy)
const PrivacyHub = lazy(() => import('./components/privacy/PrivacyHub.jsx'));

// Remaining route pages — lazy
const IntegrationsPage = lazy(() => import('./components/integrations/IntegrationsPage.jsx'));
const SoAPage = lazy(() => import('./components/soa/SoAPage.jsx'));
const ArtefactRegister = lazy(() => import('./components/artefacts/ArtefactRegister.jsx'));
const BugReports = lazy(() => import('./components/bugreport/BugReports.jsx'));
const SupportPage = lazy(() => import('./components/support/SupportPage.jsx'));
const ServiceLabPage = lazy(() => import('./components/servicelab/ServiceLabPage.jsx'));
const TenantManager = lazy(() => import('./components/superadmin/TenantManager.jsx'));
const RiskLifecyclePage = lazy(() => import('./components/risks/RiskLifecyclePage.jsx'));
const RiskAiAssessments = lazy(() => import('./components/risks/RiskAiAssessments.jsx'));
const CitedSourcePage = lazy(() => import('./components/communications/CitedSourcePage.jsx'));
const MyPrioritiesLink = lazy(() => import('./components/communications/MyPrioritiesLink.jsx'));

// Modals and small shared components — kept synchronous (needed immediately on interaction)
import OnboardingWizard from './components/onboarding/OnboardingWizard.jsx';
import EmployeeLifecycleWizard from './components/onboarding/EmployeeLifecycleWizard.jsx';
import ErrorBoundary from './components/shared/ErrorBoundary.jsx';
import { PORTAL_SUSPENSE_BG } from './styles/portalTheme.js';
import CommandMenu from './components/shared/CommandMenu.jsx';
import SuperAdminLogin from './components/superadmin/SuperAdminLogin.jsx';
import BetaTestBar from './beta/BetaTestBar.jsx';
import PRTestBar from './components/pr-testing/PRTestBar.jsx';
import { isDevEnvOnly } from './hooks/usePRTesting.js';

import { ALL_ROOT_DOMAINS, getDomainBranding } from './tenant/tenantConfig.js';
import { MODAL_REGISTRY } from './modals/modalRegistry.js';
import { rememberDeepLinkFragment } from './utils/deepLinkReturn.js';
import { hrRecruitmentAccess } from './utils/hrRecruitmentAccess.js';
import { openWorkProps } from './components/dashboard/openWorkScope.jsx';

export default function App() {
  // Hooks must be called unconditionally before any early return
  const {
    tenant,
    tenantSlug,
    isSuperAdmin,
    loading: tenantLoading,
    error: tenantError,
  } = useTenant();

  // Update browser tab title based on resolved tenant
  useEffect(() => {
    if (tenantLoading) return;
    const appName = getDomainBranding().name;
    if (isSuperAdmin) {
      document.title = `${appName} - Admin`;
    } else if (tenantSlug && tenantSlug !== '__dev__') {
      document.title = `${appName} - ${tenantSlug}`;
    } else if (tenantSlug === '__dev__') {
      document.title = `${appName} - dev`;
    } else {
      document.title = appName;
    }
  }, [tenantLoading, tenantSlug, isSuperAdmin]);

  // Safety net: redirect any *.seasuite.dk traffic to the redirect page.
  // The early script in index.html normally catches this before React loads,
  // but this handles edge cases (e.g. cached index.html without the script).
  const hostname = window.location.hostname.toLowerCase();
  if (
    (hostname === 'seasuite.dk' ||
      hostname === 'www.seasuite.dk' ||
      hostname.endsWith('.seasuite.dk')) &&
    window.location.pathname !== '/redirect.html'
  ) {
    window.location.replace('/redirect.html');
    return <div style={{ minHeight: '100vh', background: '#141210' }} />;
  }

  if (tenantLoading) return <div style={{ minHeight: '100vh', background: '#141210' }} />;

  // Registry failed to load (network error, etc.) — show a retry screen,
  // NOT NoTenant. Previously both cases showed NoTenant, making it impossible to tell
  // "tenant doesn't exist" apart from "failed to load tenant list".
  if (tenantError === 'load-failed') {
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#141210',
          flexDirection: 'column',
          gap: 16,
        }}
      >
        <div style={{ fontSize: 14, color: '#94a3b8', textAlign: 'center' }}>
          Failed to connect to workspace.
          <br />
          Check your internet connection.
        </div>
        <button
          onClick={() => window.location.reload()}
          style={{
            padding: '8px 20px',
            borderRadius: 8,
            background: '#1e293b',
            border: '1px solid #334155',
            color: '#e2e8f0',
            cursor: 'pointer',
            fontSize: 13,
          }}
        >
          Try again
        </button>
      </div>
    );
  }

  if (tenantError === 'suspended') return <SuspendedTenant />;

  if (!tenant) {
    const hostname = window.location.hostname.toLowerCase();
    const matchedDomain = ALL_ROOT_DOMAINS.find((d) => hostname === d || hostname === `www.${d}`);
    if (matchedDomain) {
      const pathname = window.location.pathname.toLowerCase();
      if (pathname === '/prices' || pathname === '/wipprices') {
        window.location.replace('/prices.html');
        return <div style={{ minHeight: '100vh', background: '#141210' }} />;
      }
      if (pathname === '/terms' || pathname === '/termscond') {
        window.location.replace('/terms.html');
        return <div style={{ minHeight: '100vh', background: '#141210' }} />;
      }
      if (pathname === '/wipland') {
        window.location.replace('/landing.html');
        return <div style={{ minHeight: '100vh', background: '#141210' }} />;
      }
      if (pathname === '/about') {
        window.location.replace('/about.html');
        return <div style={{ minHeight: '100vh', background: '#141210' }} />;
      }
      if (pathname.startsWith('/guides/')) {
        window.location.replace(`${pathname}.html`);
        return <div style={{ minHeight: '100vh', background: '#141210' }} />;
      }
      // tryggr.dk root → landing page
      if (matchedDomain === 'tryggr.dk') {
        window.location.replace('/landing.html');
        return <div style={{ minHeight: '100vh', background: '#141210' }} />;
      }
      // seasuite.dk naked domain → redirect page (rebranding to tryggr.dk)
      if (matchedDomain === 'seasuite.dk') {
        window.location.replace('/redirect.html');
        return <div style={{ minHeight: '100vh', background: '#141210' }} />;
      }
      window.location.replace('/landing.html');
      return <div style={{ minHeight: '100vh', background: '#141210' }} />;
    }
    return <NoTenant />;
  }

  if (tenant.type === 'superadmin') return <SuperAdminApp />;
  if (tenant.type === 'grc')
    return (
      <BrowserRouter>
        <GrcAuditorApp />
      </BrowserRouter>
    );
  if (tenant.type === 'login') return <UniversalLoginPage />;
  return <TenantApp tenant={tenant} />;
}

function SuperAdminApp() {
  const [adminUser, setAdminUser] = useState(null);

  const handleLogout = () => {
    localStorage.removeItem('seasuite_token_superadmin');
    localStorage.removeItem('seasuite_session_superadmin');
    setSessionToken(null);
    setAdminUser(null);
  };

  if (!adminUser) return <SuperAdminLogin onLogin={setAdminUser} />;
  return (
    <Suspense fallback={<div style={{ minHeight: '100vh', background: '#141210' }} />}>
      <ApiErrorBanner />
      <StepUpDialog api={superAdminStepUpApi} />
      <TenantManager adminUser={adminUser} onLogout={handleLogout} />
    </Suspense>
  );
}

/**
 * Rendered in place of a plan-gated route while the plan definitions this
 * tenant's gate depends on are still loading (#5654). Deliberately renders
 * INSIDE the app shell rather than replacing it with a splash: the nav and
 * sidebar stay usable, so a slow plan-definitions endpoint degrades one route
 * rather than the whole app (PLAN-B-004).
 */
function PlanGatePending() {
  return (
    <div style={{ padding: '48px 0', color: 'var(--fg2)', fontSize: 13 }}>
      Checking what your plan includes…
    </div>
  );
}

function TenantApp({ tenant }) {
  const isBetaTenant = tenant.slug?.startsWith('beta-');
  const {
    session,
    login,
    completeLogin,
    consumeHandoff,
    logout,
    registerFlushFn,
    users,
    usersLoaded,
    persistUsers,
    authReady,
    requestMagicLink,
    verifyMagicLink,
  } = useAuth(tenant.id);

  // Cross-subdomain login handoff (Phase 1). A logged-out visitor may arrive
  // with a one-time code in the URL fragment after the central login bounced
  // them here. Exchange it for a session before falling back to LoginScreen.
  // Seeded busy when a code is present so we never flash the login form first.
  const [handoffBusy, setHandoffBusy] = useState(() =>
    /[#&]code=/.test(typeof window !== 'undefined' ? window.location.hash : '')
  );
  const handoffAttempted = useRef(false);

  // Phase 3: after session is established from the bounce flow, navigate to
  // the deep-link path that was carried as ?path= through login.tryggr.dk.
  // Only fires once on the null→session transition; never re-fires.
  const postLoginPathSeen = useRef(false);
  useEffect(() => {
    if (!session || postLoginPathSeen.current) return;
    postLoginPathSeen.current = true;
    const params = new URLSearchParams(window.location.search);
    const redirectPath = params.get('path');
    if (redirectPath && redirectPath.startsWith('/') && !redirectPath.startsWith('//')) {
      window.history.replaceState(null, '', redirectPath);
    }
  }, [session]);

  useEffect(() => {
    if (!authReady || session || handoffAttempted.current) return;
    const match = window.location.hash.match(/[#&]code=([^&]+)/);
    if (!match) {
      if (handoffBusy) setHandoffBusy(false);
      return;
    }
    handoffAttempted.current = true;
    const code = decodeURIComponent(match[1]);
    // Strip the code from the URL immediately so it never lingers in history.
    window.history.replaceState(null, '', window.location.pathname + window.location.search);
    setHandoffBusy(true);
    consumeHandoff(code)
      .catch(() => {})
      .finally(() => setHandoffBusy(false));
  }, [authReady, session, consumeHandoff, handoffBusy]);
  const app = useAppState(tenant.id, session, users);
  const modals = useModalStack();

  // Wire the persistence flush so logout() can drain any pending debounced save
  // before clearing the session token (AU-3).
  useEffect(() => {
    registerFlushFn(app.flushPendingSave);
  }, [registerFlushFn, app.flushPendingSave]);

  // Wire diagnostics client to the tenant's per-tenant flag (issue #732).
  // No-op for tenants without the flag set.
  useEffect(() => {
    configureDiagnostics({
      tenantId: tenant.id || null,
      userId: session?.userId || null,
      config: tenant.diagnostics || null,
    });
  }, [tenant.id, tenant.diagnostics, session?.userId]);

  const {
    state,
    updateState,
    stateReady,
    stateLoadError,
    reloadState,
    evaluateControl,
    getControlStatus,
    forecastControlAt,
    assembleAssuranceCase,
    allControls,
    coveredIds,
    overdueTasks,
    openTasks,
    compliantDocs,
    obligationTasks,
    allTasks,
    allTasksScope,
    saveDocument,
    deleteDocument,
    retractDocument,
    saveDocumentApproval,
    reassignDocumentOwner,
    obsoleteDocument,
    recordDocumentReview,
    saveAdhocTask,
    deleteAdhocTask,
    saveVulnerabilityOverride,
    archiveVulnerabilityOverride,
    setSoftwareOwnership,
    linkVulnerabilityToRisk,
    saveRisk,
    completeRiskAction,
    completeEvidenceRequestAction,
    saveBulkRiskSnapshots,
    deleteRiskScoreSnapshot,
    retryRiskEdit,
    discardRiskEdit,
    keepMineRiskEdit,
    takeTheirsRiskEdit,
    saveAsset,
    deleteAsset,
    saveArtefact,
    deleteArtefact,
    createArtefacts,
    saveVendor: saveVendorAction,
    patchVendors: patchVendorsAction,
    patchVendorsEach: patchVendorsEachAction,
    patchVendor: patchVendorAction,
    archiveVendor,
    restoreVendor,
    deleteVendor,
    saveProduct,
    deleteProduct,
    saveEmployee,
    saveEmployeesBulk,
    deleteEmployee,
    recordAcknowledgement,
    recordCompetenceAssessment,
    addComplianceFeedback,
    logSensitiveDocAction: _logSensitiveDocAction,
    saveThreatAnalysis,
    saveControlTopic,
    deleteControlTopic,
    saveBIA,
    deleteBIA,
    overrideAssetCriticality,
    saveBcdrPlan,
    deleteBcdrPlan,
    saveBcdrRunbook,
    deleteBcdrRunbook,
    saveBcdrRunbookRun,
    deleteBcdrRunbookRun,
    saveBcdrBackup,
    deleteBcdrBackup,
    saveBcdrDrTest,
    deleteBcdrDrTest,
    saveBcdrPenTest,
    deleteBcdrPenTest,
    saveRopa,
    deleteRopa,
    saveIncident,
    deleteIncident,
    saveIncidentExercise,
    deleteIncidentExercise,
    saveManagementReview,
  } = app;

  // ── ALL hooks must be called before any early returns ──
  const saveRiskLogged = useCallback((risk) => saveRisk(risk, session), [saveRisk, session]);
  const saveBulkRiskSnapshotsLogged = useCallback(
    (label) => saveBulkRiskSnapshots(label, session),
    [saveBulkRiskSnapshots, session]
  );
  const saveDocumentLogged = useCallback(
    // GH #6303: opts is how a caller says something ABOUT this call (today:
    // "this is a retry of the save that just got refused") without smuggling it
    // through the document payload, which is the object being persisted.
    (doc, note, uid, uname, opts) => saveDocument(doc, note, uid, uname, session, opts),
    [saveDocument, session]
  );
  const deleteDocumentLogged = useCallback(
    (id, label) => deleteDocument(id, label, session),
    [deleteDocument, session]
  );
  const saveDocumentApprovalLogged = useCallback(
    (docId, decision, comment) => saveDocumentApproval(docId, decision, comment, session),
    [saveDocumentApproval, session]
  );
  const rejectDocumentApprovalLogged = useCallback(
    (docId, comment) => saveDocumentApproval(docId, 'rejected', comment, session),
    [saveDocumentApproval, session]
  );
  const reassignDocumentOwnerLogged = useCallback(
    (docId, newOwnerId, comment) => reassignDocumentOwner(docId, newOwnerId, comment, session),
    [reassignDocumentOwner, session]
  );
  const obsoleteDocumentLogged = useCallback(
    (docId, comment, supersededBy) => obsoleteDocument(docId, comment, supersededBy, session),
    [obsoleteDocument, session]
  );
  const recordDocumentReviewLogged = useCallback(
    (docId, review) => recordDocumentReview(docId, review, session),
    [recordDocumentReview, session]
  );
  const retractDocumentLogged = useCallback(
    (docId) => retractDocument(docId, session),
    [retractDocument, session]
  );
  const saveAdhocTaskLogged = useCallback(
    (task) => saveAdhocTask(task, session),
    [saveAdhocTask, session]
  );
  const deleteAdhocTaskLogged = useCallback(
    (id) => deleteAdhocTask(id, session),
    [deleteAdhocTask, session]
  );
  const setSoftwareOwnershipLogged = useCallback(
    (vendor, product, classification) =>
      setSoftwareOwnership(vendor, product, classification, session),
    [setSoftwareOwnership, session]
  );
  const linkVulnerabilityToRiskLogged = useCallback(
    (vuln, riskId) => linkVulnerabilityToRisk(vuln, riskId, session),
    [linkVulnerabilityToRisk, session]
  );

  // ── Employee save helpers ──
  // `opts` is forwarded so a terminal commit (the Add/Edit Employee modal)
  // can ask for the write to be issued now and get the edit generation back
  // for its save receipt (STG-B-003). Callers that omit it are unchanged.
  const saveEmployeeLogged = useCallback(
    (emp, opts) => saveEmployee(emp, session, opts),
    [saveEmployee, session]
  );
  const saveEmployeesBulkLogged = useCallback(
    (records) => saveEmployeesBulk(records, session),
    [saveEmployeesBulk, session]
  );
  const deleteEmployeeLogged = useCallback(
    (id, label) => deleteEmployee(id, label, session),
    [deleteEmployee, session]
  );
  const addFeedbackLogged = useCallback(
    (empId, fb) => addComplianceFeedback(empId, fb, session),
    [addComplianceFeedback, session]
  );

  // ── BIA save helpers ──
  const saveBIALogged = useCallback((bia) => saveBIA(bia, session), [saveBIA, session]);
  const deleteBIALogged = useCallback(
    (id, label) => deleteBIA(id, label, session),
    [deleteBIA, session]
  );

  // ── BCDR save helpers ──
  const saveBcdrPlanLogged = useCallback((p) => saveBcdrPlan(p, session), [saveBcdrPlan, session]);
  const deleteBcdrPlanLogged = useCallback(
    (id, label) => deleteBcdrPlan(id, label, session),
    [deleteBcdrPlan, session]
  );
  const saveBcdrRunbookLogged = useCallback(
    (r) => saveBcdrRunbook(r, session),
    [saveBcdrRunbook, session]
  );
  const deleteBcdrRunbookLogged = useCallback(
    (id, label) => deleteBcdrRunbook(id, label, session),
    [deleteBcdrRunbook, session]
  );
  const saveBcdrRunbookRunLogged = useCallback(
    (r) => saveBcdrRunbookRun(r, session),
    [saveBcdrRunbookRun, session]
  );
  const deleteBcdrRunbookRunLogged = useCallback(
    (id, label) => deleteBcdrRunbookRun(id, label, session),
    [deleteBcdrRunbookRun, session]
  );
  const saveBcdrBackupLogged = useCallback(
    (b) => saveBcdrBackup(b, session),
    [saveBcdrBackup, session]
  );
  const deleteBcdrBackupLogged = useCallback(
    (id, label) => deleteBcdrBackup(id, label, session),
    [deleteBcdrBackup, session]
  );
  const saveBcdrDrTestLogged = useCallback(
    (t) => saveBcdrDrTest(t, session),
    [saveBcdrDrTest, session]
  );
  const deleteBcdrDrTestLogged = useCallback(
    (id, label) => deleteBcdrDrTest(id, label, session),
    [deleteBcdrDrTest, session]
  );
  const saveBcdrPenTestLogged = useCallback(
    (p) => saveBcdrPenTest(p, session),
    [saveBcdrPenTest, session]
  );
  const deleteBcdrPenTestLogged = useCallback(
    (id, label) => deleteBcdrPenTest(id, label, session),
    [deleteBcdrPenTest, session]
  );

  // ── ROPA save helpers ──
  const saveRopaLogged = useCallback((entry) => saveRopa(entry, session), [saveRopa, session]);
  const deleteRopaLogged = useCallback(
    (id, label) => deleteRopa(id, label, session),
    [deleteRopa, session]
  );

  // ── Incident save helpers ──
  const saveIncidentLogged = useCallback(
    (inc, opts) => saveIncident(inc, session, opts),
    [saveIncident, session]
  );
  const deleteIncidentLogged = useCallback(
    (id, label) => deleteIncident(id, label, session),
    [deleteIncident, session]
  );
  const saveIncidentExerciseLogged = useCallback(
    (ex, opts) => saveIncidentExercise(ex, session, opts),
    [saveIncidentExercise, session]
  );
  const deleteIncidentExerciseLogged = useCallback(
    (id, label) => deleteIncidentExercise(id, label, session),
    [deleteIncidentExercise, session]
  );

  // ── Management Review save helpers ──
  const saveManagementReviewLogged = useCallback(
    (review) => saveManagementReview(review, session),
    [saveManagementReview, session]
  );

  // ── Vendor save helper — rollback-safe, engine-event-emitting version
  // lives in useRegistryActions.js (shared with saveAsset's contract) ──
  const saveVendor = useCallback(
    (vendor) => saveVendorAction(vendor, session),
    [saveVendorAction, session]
  );
  // GH #7979: field-level vendor writes (only the changed fields, each with
  // the value it held when loaded).
  const patchVendors = useCallback(
    (ids, patch, expected) => patchVendorsAction(ids, patch, expected, session),
    [patchVendorsAction, session]
  );
  const patchVendorsEach = useCallback(
    (patches, expected) => patchVendorsEachAction(patches, expected, session),
    [patchVendorsEachAction, session]
  );
  const patchVendor = useCallback(
    (base, edited) => patchVendorAction(base, edited, session),
    [patchVendorAction, session]
  );
  const archiveVendorLogged = useCallback(
    (id, label) => archiveVendor(id, label, session),
    [archiveVendor, session]
  );
  const restoreVendorLogged = useCallback(
    (id, label) => restoreVendor(id, label, session),
    [restoreVendor, session]
  );
  const deleteVendorLogged = useCallback(
    (id, label) => deleteVendor(id, label, session),
    [deleteVendor, session]
  );

  // ── Asset save helpers ──
  const saveAssetLogged = useCallback((asset) => saveAsset(asset, session), [saveAsset, session]);
  const deleteAssetLogged = useCallback(
    (id, label) => deleteAsset(id, label, session),
    [deleteAsset, session]
  );

  // ── Artefact save helpers ──
  const saveArtefactLogged = useCallback(
    (artefact) => saveArtefact(artefact, session),
    [saveArtefact, session]
  );
  const deleteArtefactLogged = useCallback(
    (id, label) => deleteArtefact(id, label, session),
    [deleteArtefact, session]
  );
  const createArtefactsLogged = useCallback(
    (artefacts) => createArtefacts(artefacts, session),
    [createArtefacts, session]
  );

  // ── Product (PDE) save helpers ──
  const saveProductLogged = useCallback(
    (product) => saveProduct(product, session),
    [saveProduct, session]
  );
  const deleteProductLogged = useCallback(
    (id, label) => deleteProduct(id, label, session),
    [deleteProduct, session]
  );

  // ── Engine — must be called before early return ──
  const engine = useEngine({ state, updateState, tenantId: tenant.id, session, users });

  // ── Plan gating — restrict modules/features by tenant plan ──
  const planGating = usePlanGating(tenant);
  // Release flag `ismsImprovement` (CI): the server's answer for this tenant.
  const improvementReleased = useReleaseFlag('ismsImprovement');

  // ── Theme — per-user, stored in database; customThemes pushed from Theme Designer ──
  const theme = useTheme(session, state.customThemes);

  // ── App version (for sidebar version badge) ──
  const [appVersion, setAppVersion] = useState(null);
  useEffect(() => {
    readAppVersion()
      .then((v) => {
        if (v) setAppVersion(v);
      })
      .catch(() => {});
  }, []);

  // ── UI layout (global per-environment selector — see src/layouts/registry.js) ──
  // Splash gates the app shell on layoutReady so the previous layout never
  // paints before the registry value resolves (avoids the BETA→Aurora flicker).
  const [layoutId, setLayoutId] = useState(DEFAULT_LAYOUT_ID);
  const [layoutReady, setLayoutReady] = useState(false);
  useEffect(() => {
    readUiLayout()
      .then((id) => {
        if (id) setLayoutId(getLayout(id).id);
      })
      .catch(() => {})
      .finally(() => setLayoutReady(true));
  }, []);

  // If the user's saved theme is not valid for the env's active version
  // (e.g. they had Midnight saved before super-admin flipped to Aurora),
  // fall the local cache back to the version's first allowed theme. Only
  // switches in-memory + localStorage — does not persist to the user
  // record, so a later flip back to Beta restores their original choice.
  // Guarded to run at most once per layoutId change — otherwise a saved
  // theme that never resolves to an allowed value (e.g. a stale/disallowed
  // id that switchTheme doesn't actually clear) would re-trigger this
  // effect on every theme.themeId change and ping-pong indefinitely.
  const themeFallbackAppliedForLayoutRef = useRef(null);
  useEffect(() => {
    if (themeFallbackAppliedForLayoutRef.current === layoutId) return;
    themeFallbackAppliedForLayoutRef.current = layoutId;
    const allowed = getVersionForLayout(layoutId).themeIds;
    if (allowed.length && !allowed.includes(theme.themeId)) {
      theme.switchTheme(allowed[0]);
    }
  }, [layoutId, theme.themeId, theme.switchTheme]);

  // ── Guide modals ──
  const [activeGuide, setActiveGuide] = useState(null);
  const handleLaunchGuide = useCallback((guideId) => setActiveGuide(guideId), []);
  const handleCloseGuide = useCallback(() => setActiveGuide(null), []);

  // ── Beta test environment reset ──
  // Preserves betaTestAnswers — resetting the environment should not wipe
  // the tester's progress/feedback on the beta questions themselves.
  //
  // Delegates to beta-environment-reset (Azure Function): documents, risks,
  // vendors, suppliers, and assets are authoritative in their own per-record
  // tables (not the tenant_state blob), so they must be reset at the store
  // level — a blob-only patch here would silently no-op on them, which is
  // exactly the bug this replaced. See beta-reset-button-validation session.
  const handleBetaReset = useCallback(
    async (categories) => {
      const result = await azurePost('/api/beta-environment-reset', { categories });
      reloadState(); // fires an async state refresh; UI updates as it lands
      return result;
    },
    [reloadState]
  );

  // ── Beta test answers sync ──
  const handleBetaAnswersChange = useCallback(
    (flatProgress) => {
      // Convert flat { "bt-01.s1": { rating, comment } } to nested { "bt-01": { "s1": { rating, comment } } }
      const nested = {};
      for (const [key, value] of Object.entries(flatProgress)) {
        const dotIdx = key.indexOf('.');
        if (dotIdx === -1) continue;
        const testId = key.slice(0, dotIdx);
        const stepId = key.slice(dotIdx + 1);
        if (!nested[testId]) nested[testId] = {};
        nested[testId][stepId] = value;
      }
      updateState((s) => {
        s.betaTestAnswers = nested;
      });
    },
    [updateState]
  );

  // ── Onboarding complete handler — must be before early returns ──
  const handleOnboardingComplete = useCallback(() => {
    if (!session) return;
    const updated = users.map((u) =>
      u.id === session.userId ? { ...u, onboarding_completed: true } : u
    );
    persistUsers(updated);
  }, [users, session, persistUsers]);

  // ── Now safe to early return ──
  if (!authReady) return <SplashScreen />;

  // While a login handoff code is being exchanged, hold on the splash so the
  // login form never flashes before the session is established.
  if (!session && handoffBusy) return <SplashScreen />;

  // Employee portal hub — accessible without a regular user session.
  // A portal-specific JWT (seasuite_portal_token_<tenantId>) is used; EmployeeHub
  // validates it on mount and redirects to /portal-login if missing or expired.
  if (window.location.pathname.toLowerCase() === '/portal') {
    const portalToken = localStorage.getItem(`seasuite_portal_token_${tenant.id}`);
    if (!portalToken) {
      window.location.replace('/portal-login');
      return null;
    }
    // ErrorBoundary, not portal UI (EMP-B-003 is untouched — nothing here
    // renders, imports or aggregates employee self-service UI). Without it a
    // render crash in the portal unmounts to a white screen and the report
    // that does reach us carries no component stack, which is the single most
    // useful part of a React crash report.
    return (
      <ErrorBoundary>
        <Suspense fallback={<div style={{ minHeight: '100vh', background: '#141210' }} />}>
          <EmployeeHub tenantId={tenant.id} tenantName={tenant.name} />
        </Suspense>
      </ErrorBoundary>
    );
  }

  // Candidate portal — accessed via magic link, no regular user session.
  // Uses a candidate-portal JWT stored under seasuite_candidate_token_<tenantId>.
  if (window.location.pathname.toLowerCase() === '/candidate-portal') {
    const candidateToken = localStorage.getItem(`seasuite_candidate_token_${tenant.id}`);
    if (!candidateToken) {
      window.location.replace('/candidate-portal-verify');
      return null;
    }
    return (
      <ErrorBoundary>
        <Suspense fallback={<div style={{ minHeight: '100vh', background: '#141210' }} />}>
          <CandidateHub tenantId={tenant.id} tenantName={tenant.name} />
        </Suspense>
      </ErrorBoundary>
    );
  }

  // Public vendor self-assessment page — token is the capability, no session.
  // Rendered for any visitor (the external vendor) on the resolved tenant.
  if (window.location.pathname.toLowerCase() === '/vendor-assessment') {
    return (
      <Suspense fallback={<div style={{ minHeight: '100vh', background: '#141210' }} />}>
        <VendorAssessmentPortal tenantId={tenant.id} />
      </Suspense>
    );
  }

  // Public subcontractor portal — same posture as /vendor-assessment above: the
  // token in the fragment is the whole capability and no session is ever issued.
  // Deliberately here, ABOVE the `if (!session)` block, not inside it: GH #5627
  // records that a magic-link route placed inside that block silently renders
  // the ordinary admin shell for a visitor who already holds a session, and a
  // supplier contact who is also a Tryggr user is exactly that case.
  if (window.location.pathname.toLowerCase() === '/supplier-portal') {
    return (
      <Suspense fallback={<div style={{ minHeight: '100vh', background: '#141210' }} />}>
        <SupplierPortal tenantId={tenant.id} />
      </Suspense>
    );
  }

  // Leader dialogue — the company's top leader, reached from the invite link
  // (/leader-dialogue?d=LD-NNNN#ldtoken=…). Same posture as /supplier-portal
  // above and for the same reason (GH #5627): the link is the credential, it
  // mints a leader-dialogue session bound to one dialogue (LDLG-B-005) and
  // never touches an admin session, so it sits ABOVE the `if (!session)`
  // block — the leader is often also a Tryggr user. No app chrome.
  if (window.location.pathname.toLowerCase() === '/leader-dialogue') {
    return (
      <ErrorBoundary>
        <Suspense fallback={<div style={{ minHeight: '100vh', background: PORTAL_SUSPENSE_BG }} />}>
          <LeaderDialogueRoute tenantId={tenant.id} />
        </Suspense>
      </ErrorBoundary>
    );
  }

  // Employee/candidate magic-link routes — accessible without a regular user
  // session, same as /portal and /candidate-portal above, and for the same
  // reason (GH #5627): these three used to sit inside the `if (!session)`
  // block below, so a magic link sent to someone who is ALSO a Tryggr user
  // (routinely the person who set the tenant up) fell straight through
  // whenever their browser already held an admin session — the link
  // silently rendered the ordinary admin shell instead of verifying, with no
  // error and no explanation. Verifying a magic link mints a separate
  // `seasuite_portal_token_<tenantId>` / `seasuite_candidate_token_<tenantId>`
  // and never touches the admin session (EmployeeMagicLinkVerify's own
  // success path is `window.location.replace('/portal')`, handled
  // unconditionally above), so honouring these routes with a session present
  // does not cross EMP-B-003 — it's the same isolated EmployeeHub/CandidateHub
  // surface as /portal and /candidate-portal, just reachable.
  {
    const pathname = window.location.pathname.toLowerCase();

    if (pathname === '/portal-login') {
      return (
        <EmployeeMagicLinkRequest
          requestMagicLink={requestMagicLink}
          onBack={() => {
            window.location.href = '/';
          }}
        />
      );
    }

    if (pathname === '/portal-verify') {
      return <EmployeeMagicLinkVerify verifyMagicLink={verifyMagicLink} />;
    }

    if (pathname === '/candidate-portal-verify') {
      return (
        <CandidateMagicLinkVerify
          tenantId={tenant.id}
          onVerified={(token) => {
            localStorage.setItem(`seasuite_candidate_token_${tenant.id}`, token);
            window.location.replace('/candidate-portal');
          }}
        />
      );
    }
  }

  // Pre-auth routes
  if (!session) {
    const pathname = window.location.pathname.toLowerCase();

    // ── Phase 3: SSO callback ──────────────────────────────────────────────
    // Microsoft Entra redirects back to {slug}.tryggr.dk/sso/callback with
    // ?code=...&state=... after the user authenticates. Always render
    // SsoCallback here regardless of bounce eligibility so the OIDC exchange
    // completes on the correct slug origin where sessionStorage state lives.
    if (pathname === '/sso/callback') {
      return (
        <SsoCallback
          tenantId={tenant.id}
          onSessionEstablished={() => window.location.replace('/')}
        />
      );
    }

    // ── Phase 3: central login bounce ─────────────────────────────────────
    // On real prod {slug}.tryggr.dk, logged-out visitors are sent to
    // login.tryggr.dk so they authenticate once on the central page and are
    // handed back via the #code= handoff mechanism.
    //
    // Bounce is SKIPPED for:
    //   • handoffBusy (already caught above — splash while #code= exchanges)
    //   • ?username= — returning from central login; LoginScreen handles auth
    //   • ?mfaRecovery= — one-time tenant-specific recovery link
    //   • ?fallback_token= — emergency admin access token
    //   • /sso/callback — handled above (OIDC return from Microsoft Entra)
    //   • mydev/mytest env portals (resolved.isEnvPortal)
    //   • custom/distributor domains (resolved.isCustomDomain)
    //   • localhost / dev fallback
    const bounceHost = window.location.hostname.toLowerCase();
    const bounceIsTryggrSub = bounceHost.endsWith('.tryggr.dk');
    const bounceSub = bounceIsTryggrSub
      ? bounceHost.slice(0, bounceHost.length - '.tryggr.dk'.length)
      : '';
    const bounceIsRealProd =
      bounceIsTryggrSub &&
      bounceSub &&
      !['admin', 'admin-test', 'login', 'grc', 'mydev', 'mytest', 'www'].includes(bounceSub);

    if (bounceIsRealProd) {
      const bounceParams = new URLSearchParams(window.location.search);
      const hasException =
        bounceParams.has('username') ||
        bounceParams.has('mfaRecovery') ||
        bounceParams.has('mfaSetupReset') ||
        bounceParams.has('fallback_token');

      if (!hasException) {
        const returnPath = window.location.pathname + window.location.search;
        // The fragment cannot ride the bounce (the hand-off returns in #code=);
        // a version-pinned deep link keeps its #anchor this way (ACEA-03b).
        rememberDeepLinkFragment();
        const bounceTarget =
          `https://login.tryggr.dk` +
          `?return=${encodeURIComponent(bounceSub)}` +
          `&path=${encodeURIComponent(returnPath)}`;
        window.location.replace(bounceTarget);
        return <SplashScreen />;
      }
    }

    return (
      <LoginScreen
        tenantId={tenant.id}
        login={login}
        completeLogin={completeLogin}
        tenant={tenant}
      />
    );
  }

  // Tenant state failed to load after retries (e.g. a transient 502 from the
  // Function App) — show a retry screen instead of hanging on the splash
  // screen forever with no user-facing signal (#4915).
  if (stateLoadError === 'load-failed') {
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#141210',
          flexDirection: 'column',
          gap: 16,
        }}
      >
        <div style={{ fontSize: 14, color: '#94a3b8', textAlign: 'center' }}>
          Failed to load your workspace data.
          <br />
          Check your internet connection.
        </div>
        <button
          onClick={() => window.location.reload()}
          style={{
            padding: '8px 20px',
            borderRadius: 8,
            background: '#1e293b',
            border: '1px solid #334155',
            color: '#e2e8f0',
            cursor: 'pointer',
            fontSize: 13,
          }}
        >
          Try again
        </button>
      </div>
    );
  }

  if (!stateReady || !layoutReady) return <SplashScreen />;

  const currentUser = users.find((u) => u.id === session.userId);
  const showOnboarding = currentUser && !currentUser.onboarding_completed;

  const role = session.role;
  const canEditContent = canEdit(role);
  const canAdminContent = canAdmin(role);
  const isProspectUser = isProspect(role);
  const seasuiteRole = resolveSeasuiteRole(session?.userId, users, state?.seasuiteRoles);
  const modulePerm = getModulePermissions(session, seasuiteRole);

  // Route guard: redirect to dashboard (or /gap for prospects) when access is denied
  const guardModule = (moduleKey, element) => {
    if (isProspectUser) return <Navigate to="/gap" replace />;
    if (modulePerm[moduleKey]?.access === false) return <Navigate to="/dashboard" replace />;
    // PLAN-B-001 is a fail-safe boundary, and "allowed" is not an answer this
    // gate is entitled to give before the plan definitions are actually known.
    // usePlanGating answers from DEFAULT_PLAN_DEFINITIONS while it is still
    // loading — a reasonable baseline for deciding what to OFFER in the nav,
    // and an open door for deciding what to RENDER. A module the tenant's plan
    // disables would mount, run its effects and paint, and only be redirected
    // away once the real definitions landed a moment later (#5654). Hold the
    // route until the answer is real instead. The app shell stays painted
    // around this, and the load is bounded in usePlanGating (PLAN-B-004: a
    // plan-definitions load that never answers must not stall the app), so
    // this placeholder always resolves to a decision.
    if (planGating.loading) return <PlanGatePending />;
    if (!planGating.isModuleAllowed(moduleKey)) return <Navigate to="/dashboard" replace />;
    // Release flag `ismsImprovement` (RFLG-B-004: AND with the plan gate): every
    // route of the Improvement module answers to it and shows the neutral
    // "not switched on" notice while it is off (the server enforces it too).
    if (moduleKey === 'improvement') {
      return <ReleaseFlagGate flag="ismsImprovement">{element}</ReleaseFlagGate>;
    }
    return element;
  };

  // Supplier chain hub — the same element behind /customers,
  // /delivery-agreements and /subcontractors. tabAccess mirrors the route
  // guards so a tab the tenant cannot reach is never offered: switching tabs
  // must never land the user on a redirect.
  const supplierChainTabAccess = {
    customers: modulePerm['customers']?.access !== false && planGating.isModuleAllowed('customers'),
    subcontractors:
      modulePerm['subcontractors']?.access !== false &&
      planGating.isModuleAllowed('subcontractors'),
  };
  const supplierChainHub = (
    <SupplierChainHub
      state={state}
      session={session}
      pushModal={modals.push}
      saveVendor={saveVendor}
      canAdmin={canAdminContent}
      tabAccess={supplierChainTabAccess}
    />
  );

  // Executive toggle is shown when the user is an admin AND the executive module
  // is permitted by both the module permission system and plan gating.
  const showExecToggle =
    canAdminContent &&
    modulePerm['executive']?.access !== false &&
    planGating.isModuleAllowed('executive');
  // msr 4.10c (D28-A): with the publish switch on, the Executive view is the
  // "Executive dashboard" role permission (admin by default, grantable to a
  // manager or reader) instead of admin-only — plan gated on `executive` and
  // on `improvement` (the server reads view=exec under both). DashboardLayout
  // uses it only while the server says the switch is on; the server decides.
  const execDashboardAllowed =
    modulePerm[EXEC_DASHBOARD_MODULE]?.access === true &&
    planGating.isModuleAllowed('executive') &&
    planGating.isModuleAllowed('improvement') &&
    improvementReleased;
  const guardAdmin = (element) => {
    if (!canAdminContent) return <Navigate to="/dashboard" replace />;
    return element;
  };

  // msr 4.4: a read-only screen for managers as well as admins (Objectives).
  const guardAdminOrManager = (element) => {
    if (!canAdminContent && session?.role !== 'manager')
      return <Navigate to="/dashboard" replace />;
    return element;
  };

  // GH #7057: read from the user's own record, never the session — the
  // session never carried the grant, so every non-super-admin was redirected.
  // While the user list is still loading the answer is unknown: render nothing
  // rather than redirect, or a deep link to /candidates bounces on every load.
  const hrAccess = hrRecruitmentAccess(session, users, usersLoaded);
  const guardHR = (element) => {
    if (hrAccess === null) return null;
    if (!hrAccess) return <Navigate to="/dashboard" replace />;
    return element;
  };

  const commonProps = {
    state,
    updateState,
    // Terminal record commits (a modal that closes on save) issue their write
    // on the click instead of waiting out the debounce — see useCommitNow.js.
    flushPendingSave: app.flushPendingSave,
    reloadState,
    session,
    users,
    tenant,
    canEdit: canEditContent,
    canAdmin: canAdminContent,
    showExecToggle,
    execDashboardAllowed,
    evaluateControl,
    getControlStatus,
    forecastControlAt,
    assembleAssuranceCase,
    allControls,
    coveredIds,
    overdueTasks,
    openTasks,
    compliantDocs,
    obligationTasks,
    // GH #7583: the list travels with its scope, or every task widget reads 'loading'.
    ...openWorkProps({ allTasks, allTasksScope }),
    saveControlTopic,
    deleteControlTopic,
    saveDocument: saveDocumentLogged,
    deleteDocument: deleteDocumentLogged,
    reassignDocumentOwner: reassignDocumentOwnerLogged,
    obsoleteDocument: obsoleteDocumentLogged,
    recordDocumentReview: recordDocumentReviewLogged,
    // The Artefacts register is a ROUTED page, so it is handed commonProps and
    // nothing else. These three were wired only into the modal-registry ctx
    // below, which meant <ArtefactRegister {...commonProps} /> received
    // saveArtefact === undefined and every create threw "saveArtefact is not a
    // function" the moment the user pressed Create Artefact. The throw is a
    // TypeError, and writeErrors.js classifies any TypeError as a transport
    // failure, so what the user saw was "No connection to the server — your
    // artefact has not been saved. Check your connection and try again." No
    // request was ever issued. GH #6163.
    saveArtefact: saveArtefactLogged,
    deleteArtefact: deleteArtefactLogged,
    createArtefacts: createArtefactsLogged,
    saveAdhocTask: saveAdhocTaskLogged,
    deleteAdhocTask: deleteAdhocTaskLogged,
    saveVulnerabilityOverride,
    archiveVulnerabilityOverride,
    setSoftwareOwnership: setSoftwareOwnershipLogged,
    linkVulnerabilityToRisk: linkVulnerabilityToRiskLogged,
    saveRisk: saveRiskLogged,
    completeRiskAction,
    completeEvidenceRequestAction,
    saveBulkRiskSnapshots: saveBulkRiskSnapshotsLogged,
    deleteRiskScoreSnapshot,
    saveThreatAnalysis,
  };

  // Superset context passed to every modal's registry entry. Kept separate
  // from commonProps (which is spread onto routed pages) so no page
  // component's prop surface changes as a side effect of this registry.
  const modalContext = {
    ...commonProps,
    pushModal: modals.push,
    popModal: modals.pop,
    approveDocument: saveDocumentApprovalLogged,
    rejectDocument: rejectDocumentApprovalLogged,
    retractDocument: retractDocumentLogged,
    // useDocumentState's guarded documents-workflow wrapper. The editor and the
    // review hub must call transitions through this, not storage-azure's raw
    // callDocumentsWorkflow — it claims tenant_state's row version for the
    // duration of the call so the 1.5s debounced full-state save can't fire
    // mid-transition with a version the transition has already superseded
    // (GH #5314).
    callWorkflow: app.callWorkflow,
    adoptServerDocument: app.adoptServerDocument,
    theme,
    layoutId,
    persistUsers,
    retryRiskEdit,
    discardRiskEdit,
    keepMineRiskEdit,
    takeTheirsRiskEdit,
    appVersion,
    // The workflow step task opens its run only where the run view is reachable (PDWB-02).
    planGating,
    saveBIA: saveBIALogged,
    deleteBIA: deleteBIALogged,
    overrideAssetCriticality,
    saveBcdrPlan: saveBcdrPlanLogged,
    deleteBcdrPlan: deleteBcdrPlanLogged,
    saveBcdrRunbook: saveBcdrRunbookLogged,
    deleteBcdrRunbook: deleteBcdrRunbookLogged,
    saveBcdrRunbookRun: saveBcdrRunbookRunLogged,
    deleteBcdrRunbookRun: deleteBcdrRunbookRunLogged,
    saveBcdrBackup: saveBcdrBackupLogged,
    deleteBcdrBackup: deleteBcdrBackupLogged,
    saveBcdrDrTest: saveBcdrDrTestLogged,
    deleteBcdrDrTest: deleteBcdrDrTestLogged,
    saveBcdrPenTest: saveBcdrPenTestLogged,
    deleteBcdrPenTest: deleteBcdrPenTestLogged,
    saveVendor,
    patchVendors,
    patchVendor,
    archiveVendor: archiveVendorLogged,
    restoreVendor: restoreVendorLogged,
    deleteVendor: deleteVendorLogged,
    saveEmployee: saveEmployeeLogged,
    saveEmployeesBulk: saveEmployeesBulkLogged,
    addFeedback: addFeedbackLogged,
    deleteEmployee: deleteEmployeeLogged,
    recordAcknowledgement,
    recordCompetenceAssessment,
    saveAsset: saveAssetLogged,
    deleteAsset: deleteAssetLogged,
    // saveArtefact / deleteArtefact / createArtefacts now come in with the
    // ...commonProps spread above. Re-listing them here would be two copies of
    // one wiring kept in step by hand — the shape useAppState.js:402 records
    // this codebase being bitten by once already, when two sub-hooks both
    // exported a saveArtefact and the working one won only on spread order.
    saveProduct: saveProductLogged,
    deleteProduct: deleteProductLogged,
    saveRopa: saveRopaLogged,
    deleteRopa: deleteRopaLogged,
    saveIncident: saveIncidentLogged,
    deleteIncident: deleteIncidentLogged,
    saveIncidentExercise: saveIncidentExerciseLogged,
    deleteIncidentExercise: deleteIncidentExerciseLogged,
    saveManagementReview: saveManagementReviewLogged,
  };

  function renderModal(m, _idx) {
    const close = () => modals.pop();
    const ModalEntry = MODAL_REGISTRY[m.type];
    if (!ModalEntry) {
      // An unregistered type is a button that opens nothing. Say so, rather
      // than fail silently as "+ Add Involvement" did for months (br12-01);
      // modalRegistry.coverage.test.js catches the literal pushes in CI.
      console.error(`[modals] no modal is registered for type "${m.type}" — nothing was opened`);
      return null;
    }
    return <ModalEntry modal={m} ctx={modalContext} close={close} />;
  }

  const isAurora = getVersionForLayout(layoutId).id === 'v2';
  // Within Aurora the user picks light vs dark via their theme; default
  // to dark when the user's theme is not an Aurora variant (e.g. on first
  // load before they have picked).
  const auroraVars = theme.themeId === 'aurora-light' ? AURORA_LIGHT_VARS : AURORA_DARK_VARS;
  const shellStyle = isAurora
    ? { display: 'flex', flexDirection: 'column', height: '100vh', ...auroraVars }
    : { display: 'flex', flexDirection: 'column', height: '100vh' };

  const enabledFrameworkIds = (state.frameworks || [])
    .filter((f) => f.enabled !== false)
    .map((f) => f.id);

  return (
    <BrowserRouter>
      <ControlFrameworkPreferenceProvider
        preferredFrameworkId={currentUser?.preferredFrameworkId}
        enabledFrameworkIds={enabledFrameworkIds}
      >
        <ErrorBoundary session={session} tenant={tenant}>
          <div style={shellStyle}>
            <TopBannerStack>
              {/* Beta test environment banner — only for beta-XXXX tenants */}
              {isBetaTenant && (
                <div className="beta-env-banner" style={{ pointerEvents: 'auto' }}>
                  <span
                    style={{
                      width: 7,
                      height: 7,
                      borderRadius: '50%',
                      background: '#a78bfa',
                      animation: 'betaPulse 2s ease-in-out infinite',
                    }}
                  />
                  BETA TEST ENVIRONMENT — All data is for testing purposes only
                </div>
              )}
              <EnvironmentBanner />
              <SaveConflictBanner />
              <ServiceRestartBanner />
              <WriteErrorBanner />
              <StaleStateBanner />
              <ApiErrorBanner />
              <StepUpDialog tenantId={tenant.id} />
              <AccessApprovalBanner
                tenantId={tenant.id}
                session={session}
                state={state}
                updateState={updateState}
              />
              <PaymentWarningBanner tenantId={tenant.id} session={session} />
              <GlobalRegulatoryAlertBanner state={state} session={session} />
            </TopBannerStack>
            {isAurora && (
              <TopBar
                session={session}
                state={state}
                tenant={tenant}
                onLogout={logout}
                onUserSettings={() => modals.push({ type: 'user-settings' })}
                pushModal={modals.push}
                appVersion={appVersion}
                users={users}
                obligationTasks={app.obligationTasks}
              />
            )}
            <div style={{ display: 'flex', minHeight: 0, flex: 1, background: 'var(--bg)' }}>
              {isAurora ? (
                <AuroraSidebar
                  session={session}
                  canHR={hrAccess === true}
                  state={state}
                  tenant={tenant}
                  canAdmin={canAdminContent}
                  planGating={planGating}
                />
              ) : (
                <Sidebar
                  session={session}
                  canHR={hrAccess === true}
                  state={state}
                  tenant={tenant}
                  onLogout={logout}
                  onUserSettings={() => modals.push({ type: 'user-settings' })}
                  pushModal={modals.push}
                  canAdmin={canAdminContent}
                  appVersion={appVersion}
                  users={users}
                  persistUsers={persistUsers}
                  planGating={planGating}
                  obligationTasks={app.obligationTasks}
                />
              )}
              {/* minWidth: 0 lets this flex item shrink below its content's
                  min-content width; without it a wide table or form pushes the
                  whole shell past the viewport and the page body scrolls
                  sideways. overflowX keeps genuinely-wide content scrolling
                  inside main instead. */}
              <main
                style={{
                  flex: 1,
                  minWidth: 0,
                  padding: '32px 40px',
                  overflowY: 'auto',
                  overflowX: 'auto',
                }}
              >
                <ErrorBoundary session={session} tenant={tenant}>
                  <Suspense fallback={<div style={{ minHeight: '100%' }} />}>
                    <Routes>
                      <Route
                        path="/"
                        element={<Navigate to={isProspectUser ? '/gap' : '/dashboard'} replace />}
                      />
                      {/* Where every task notice and digest links (ACEA-07): opens the
                          Action Centre (My priorities) for the signed-in person. */}
                      <Route
                        path="/my-priorities"
                        element={<MyPrioritiesLink pushModal={modals.push} />}
                      />
                      <Route
                        path="/dashboard"
                        element={guardModule(
                          'dashboard',
                          <Dashboard {...commonProps} pushModal={modals.push} />
                        )}
                      />
                      <Route
                        path="/next-steps"
                        element={guardModule('nextsteps', <NextStepsPage {...commonProps} />)}
                      />
                      <Route
                        path="/guides"
                        element={guardModule('guides', <GuidesPage {...commonProps} />)}
                      />
                      <Route
                        path="/guides/obligation-coverage"
                        element={guardModule(
                          'guides',
                          <ObligationCoverageGuide {...commonProps} />
                        )}
                      />
                      <Route
                        path="/documents"
                        element={guardModule(
                          'documents',
                          <Documents
                            {...commonProps}
                            pushModal={modals.push}
                            setModal={(m) => modals.push(m)}
                          />
                        )}
                      />
                      <Route
                        path="/tasks"
                        element={guardModule(
                          'tasks',
                          <Tasks {...commonProps} pushModal={modals.push} />
                        )}
                      />
                      <Route
                        path="/controls"
                        element={guardModule(
                          'controls',
                          <ControlsModule
                            {...commonProps}
                            pushModal={modals.push}
                            setState={updateState}
                            setModal={(m) => modals.push(m)}
                          />
                        )}
                      />
                      <Route
                        path="/soa"
                        element={guardModule('soa', <SoAPage {...commonProps} />)}
                      />
                      <Route
                        path="/artefacts"
                        element={guardModule('artefacts', <ArtefactRegister {...commonProps} />)}
                      />
                      <Route
                        path="/frameworks"
                        element={
                          isProspectUser ? (
                            <Navigate to="/gap" replace />
                          ) : (
                            <FrameworkManager {...commonProps} />
                          )
                        }
                      />
                      <Route
                        path="/risks"
                        element={guardModule(
                          'risks',
                          <Risks {...commonProps} setModal={(m) => modals.push(m)} />
                        )}
                      />
                      {/* Version-pinned deep links (ACEA-03b, COMM-B-013):
                          /<module>/<recordId>?v=<version>#<anchor>. Each sits behind
                          its module's own guard (evidence is risk-scoped); the server
                          decides whether the cited version opens for this person. */}
                      <Route
                        path="/documents/:recordId"
                        element={guardModule('documents', <CitedSourcePage module="documents" />)}
                      />
                      <Route
                        path="/risks/:recordId"
                        element={guardModule('risks', <CitedSourcePage module="risks" />)}
                      />
                      <Route
                        path="/evidence/:recordId"
                        element={guardModule('risks', <CitedSourcePage module="evidence" />)}
                      />
                      <Route
                        path="/artefacts/:recordId"
                        element={guardModule('artefacts', <CitedSourcePage module="artefacts" />)}
                      />
                      <Route
                        path="/tasks/:recordId"
                        element={guardModule('tasks', <CitedSourcePage module="tasks" />)}
                      />
                      <Route
                        path="/controls/:recordId"
                        element={guardModule('controls', <CitedSourcePage module="controls" />)}
                      />
                      {/* RAI-07: the AI assessment decision log. A static segment, so it
                          ranks above /risks/:recordId. */}
                      <Route
                        path="/risks/ai-assessments"
                        element={guardModule(
                          'risks',
                          <ReleaseFlagGate flag="riskAiAssessment">
                            <RiskAiAssessments {...commonProps} />
                          </ReleaseFlagGate>
                        )}
                      />
                      <Route
                        path="/risks/:id/lifecycle"
                        element={guardModule(
                          'risks',
                          <RiskLifecyclePage {...commonProps} pushModal={modals.push} />
                        )}
                      />
                      <Route
                        path="/ai-governance"
                        element={guardModule(
                          'ai-governance',
                          <ReleaseFlagGate flag="aiGovernance">
                            <AIGovernancePage {...commonProps} pushModal={modals.push} />
                          </ReleaseFlagGate>
                        )}
                      />
                      <Route
                        path="/threats"
                        element={guardModule(
                          'threats',
                          <ThreatsPage {...commonProps} pushModal={modals.push} />
                        )}
                      />
                      <Route
                        path="/vulnerabilities"
                        element={guardModule(
                          'vulnerabilities',
                          <VulnerabilitiesPage {...commonProps} pushModal={modals.push} />
                        )}
                      />
                      <Route
                        path="/priority"
                        element={guardModule('priority', <PriorityView {...commonProps} />)}
                      />
                      <Route
                        path="/findings"
                        element={guardModule(
                          'findings',
                          <FindingsPage {...commonProps} pushModal={modals.push} />
                        )}
                      />
                      <Route
                        path="/vendors"
                        element={guardModule(
                          'vendors',
                          <VendorsPage
                            {...commonProps}
                            pushModal={modals.push}
                            patchVendors={patchVendors}
                            patchVendorsEach={patchVendorsEach}
                          />
                        )}
                      />
                      {/* Supplier chain — Customers → Delivery Agreements →
                          Subcontractors is one ordered sequence, so it is one
                          tabbed page at /customers?tab=. The two older routes
                          keep their own module guard and redirect onto the
                          right tab, so existing links stay valid. */}
                      <Route
                        path="/customers"
                        element={guardModule('customers', supplierChainHub)}
                      />
                      <Route
                        path="/delivery-agreements"
                        element={guardModule(
                          'customers',
                          <Navigate to="/customers?tab=delivery-agreements" replace />
                        )}
                      />
                      <Route
                        path="/subcontractors"
                        element={guardModule(
                          'subcontractors',
                          <Navigate to="/customers?tab=subcontractors" replace />
                        )}
                      />
                      <Route
                        path="/agreements-register"
                        element={guardModule(
                          'agreements-register',
                          <AgreementsPage {...commonProps} />
                        )}
                      />
                      <Route
                        path="/privacy"
                        element={guardModule(
                          'privacy',
                          <PrivacyHub {...commonProps} pushModal={modals.push} />
                        )}
                      />
                      <Route path="/ropa" element={<Navigate to="/privacy?tab=ropa" replace />} />
                      <Route path="/dpia" element={<Navigate to="/privacy?tab=dpia" replace />} />
                      <Route path="/dsr" element={<Navigate to="/privacy?tab=dsr" replace />} />
                      <Route
                        path="/consent"
                        element={<Navigate to="/privacy?tab=consent" replace />}
                      />
                      <Route
                        path="/legal-register"
                        element={<Navigate to="/privacy?tab=legal" replace />}
                      />
                      <Route path="/dpo" element={<Navigate to="/privacy?tab=dpo" replace />} />
                      <Route
                        path="/bia"
                        element={guardModule(
                          'bia',
                          <BIAPage {...commonProps} pushModal={modals.push} />
                        )}
                      />
                      <Route
                        path="/bcdr"
                        element={guardModule(
                          'bcdr',
                          <BCDRPage {...commonProps} pushModal={modals.push} />
                        )}
                      />
                      <Route
                        path="/management-review"
                        element={guardModule(
                          'management-review',
                          <ManagementReviewPage {...commonProps} pushModal={modals.push} />
                        )}
                      />
                      <Route
                        path="/management-review/leader-dialogue"
                        element={guardModule(
                          'management-review',
                          <ReleaseFlagGate flag="leaderDialogue">
                            <LeaderDialoguePage {...commonProps} />
                          </ReleaseFlagGate>
                        )}
                      />
                      <Route
                        path="/employees"
                        element={guardModule(
                          'employees',
                          <EmployeesPage
                            {...commonProps}
                            pushModal={modals.push}
                            onAddFeedback={addFeedbackLogged}
                          />
                        )}
                      />
                      <Route
                        path="/candidates"
                        element={guardHR(
                          <CandidatesPage
                            state={state}
                            updateState={updateState}
                            session={session}
                            users={users}
                          />
                        )}
                      />
                      <Route
                        path="/assets"
                        element={guardModule(
                          'assets',
                          <AssetsPage {...commonProps} pushModal={modals.push} />
                        )}
                      />
                      <Route
                        path="/products"
                        element={guardModule(
                          'products',
                          <ProductsPage {...commonProps} pushModal={modals.push} />
                        )}
                      />
                      <Route
                        path="/incidents"
                        element={guardModule(
                          'incidents',
                          <IncidentsPage
                            {...commonProps}
                            pushModal={modals.push}
                            saveIncident={saveIncidentLogged}
                            deleteIncident={deleteIncidentLogged}
                            saveExercise={saveIncidentExerciseLogged}
                            deleteExercise={deleteIncidentExerciseLogged}
                          />
                        )}
                      />
                      <Route
                        path="/calendar"
                        element={guardModule(
                          'calendar',
                          <ComplianceCalendar
                            state={state}
                            pushModal={modals.push}
                            users={users}
                            obligationTasks={app.obligationTasks}
                            workflowSteps={app.workflowSteps}
                            haltReviews={app.haltReviews}
                            riskAiTasks={app.riskAiTasks}
                            allTasksScope={app.allTasksScope}
                            session={session}
                            tenant={tenant}
                          />
                        )}
                      />
                      <Route
                        path="/gap"
                        element={<GapAnalysisPage {...commonProps} pushModal={modals.push} />}
                      />
                      <Route path="/grc" element={<GrcClientPortal />} />
                      <Route path="/log" element={guardAdmin(<ActivityLog {...commonProps} />)} />
                      <Route
                        path="/security-events"
                        element={guardAdmin(<SecurityEvents tenant={tenant} session={session} />)}
                      />
                      {/* msr 4.9: the internal executive and domain views — admin only,
                          behind the `improvement` role module and plan gate, and
                          allow-listed by the server (MSR_EXEC_VIEW_TENANTS, plan
                          D1 A): a tenant off the list is sent back to the
                          dashboard. No sidebar entry until 4.10 publishes them. */}
                      <Route
                        path="/improvement"
                        element={guardAdmin(
                          guardModule(
                            'improvement',
                            <ImprovementPage view="exec" session={session} users={users} />
                          )
                        )}
                      />
                      <Route
                        path="/improvement/domain/:domainId"
                        element={guardAdmin(
                          guardModule(
                            'improvement',
                            <ImprovementPage view="domain" session={session} users={users} />
                          )
                        )}
                      />
                      {/* msr 2.7 (GH #7130): internal Metric Explorer — admin only,
                          behind the `improvement` role module and plan gate. */}
                      <Route
                        path="/improvement/explorer"
                        element={guardAdmin(
                          guardModule(
                            'improvement',
                            <MetricExplorerPage view="list" session={session} users={users} />
                          )
                        )}
                      />
                      <Route
                        path="/improvement/explorer/metric/:metricId"
                        element={guardAdmin(
                          guardModule(
                            'improvement',
                            <MetricExplorerPage view="metric" session={session} users={users} />
                          )
                        )}
                      />
                      {/* msr 3.4 (GH #7176): the recurrence review queue — same gates. */}
                      <Route
                        path="/improvement/recurrence"
                        element={guardAdmin(
                          guardModule(
                            'improvement',
                            <RecurrenceReviewPage session={session} users={users} />
                          )
                        )}
                      />
                      {/* msr 4.3: the context-change register. Module and plan gate
                          only — any tenant user may read it, and the approver a
                          "no change needed" request names may be a non-admin
                          holder of the Approver role; the server decides every action. */}
                      <Route
                        path="/improvement/context-changes"
                        element={guardModule(
                          'improvement',
                          <ContextChangeRegisterPage session={session} users={users} />
                        )}
                      />
                      {/* msr 4.4: information security objectives. Admins set
                          and review them; managers read them (founder decision
                          B on PR #7407). The server decides every action. */}
                      {/* PDWB-01: workflow definitions and the designer. Admins
                          design and publish; managers read. The server decides
                          every action (workflow-definitions-read / -write). */}
                      <Route
                        path="/workflows"
                        element={guardAdminOrManager(
                          guardModule(
                            'workflows',
                            <ReleaseFlagGate flag="policyWorkflows">
                              <WorkflowsPage {...commonProps} pushModal={modals.push} />
                            </ReleaseFlagGate>
                          )
                        )}
                      />
                      <Route
                        path="/improvement/objectives"
                        element={guardAdminOrManager(
                          guardModule(
                            'improvement',
                            <ObjectivesPage session={session} users={users} />
                          )
                        )}
                      />
                      <Route
                        path="/improvement/explorer/evidence"
                        element={guardAdmin(
                          guardModule(
                            'improvement',
                            <MetricExplorerPage view="evidence" session={session} users={users} />
                          )
                        )}
                      />
                      <Route
                        path="/breach-monitor"
                        element={guardModule(
                          'email-breach-monitoring',
                          <EmailBreachMonitor session={session} canAdmin={canAdminContent} />
                        )}
                      />
                      <Route
                        path="/users"
                        element={guardAdmin(
                          <UserManagement
                            {...commonProps}
                            persistUsers={persistUsers}
                            welcomeEmailEnabled={!!state.emailSettings?.welcomeEmailEnabled}
                          />
                        )}
                      />
                      <Route
                        path="/service-lab"
                        element={guardAdmin(<ServiceLabPage session={session} />)}
                      />
                      <Route path="/executive" element={<Navigate to="/dashboard" replace />} />
                      <Route path="/bugs" element={guardModule('bugs', <BugReports />)} />
                      {/*
                        Support is deliberately NOT behind guardModule: a
                        customer who cannot reach us because of their plan tier
                        is a support failure, not a gated feature. Every other
                        route here gates on the plan; this one never does.
                      */}
                      <Route path="/support" element={<SupportPage session={session} />} />
                      <Route
                        path="/integrations"
                        element={guardModule(
                          'integrations',
                          <IntegrationsPage {...commonProps} tenantId={tenant?.id} />
                        )}
                      />
                      <Route
                        path="/settings"
                        element={
                          isProspectUser ? (
                            <Navigate to="/gap" replace />
                          ) : (
                            <Settings
                              {...commonProps}
                              persistUsers={persistUsers}
                              engine={engine}
                              onLaunchGuide={handleLaunchGuide}
                              saveAdhocTask={saveAdhocTaskLogged}
                            />
                          )
                        }
                      />
                    </Routes>
                  </Suspense>
                </ErrorBoundary>
              </main>

              {modals.stack.map((m, idx) => (
                <ErrorBoundary key={idx} session={session} tenant={tenant}>
                  {/*
                    A lazy modal chunk that is not cached yet takes a network
                    round-trip to arrive. With a null fallback the pushed modal
                    has no presence at all during that window: the screen does
                    not change, and the modal underneath stays clickable — so a
                    click meant for it lands on a surface that is about to be
                    covered, and the user gets no sign their first click did
                    anything. Rendering the backdrop immediately makes the push
                    visible the moment it happens and keeps the stack's top
                    entry the topmost thing on screen throughout (#5598).
                    Only ever seen on a modal type's first open in a session —
                    once the chunk is cached React resolves it synchronously.
                  */}
                  <Suspense
                    fallback={
                      <div
                        className="modal-backdrop"
                        style={{ zIndex: 700 + idx * 10 }}
                        aria-busy="true"
                        onClick={(e) => {
                          // Same escape hatch a real modal's backdrop gives, so a
                          // chunk that never arrives cannot trap the user. Guarded
                          // to the top entry — pop() always removes the last one.
                          if (e.target === e.currentTarget && idx === modals.stack.length - 1) {
                            modals.pop();
                          }
                        }}
                      />
                    }
                  >
                    <ModalStackDepthContext.Provider value={idx}>
                      {renderModal(m, idx)}
                    </ModalStackDepthContext.Provider>
                  </Suspense>
                </ErrorBoundary>
              ))}

              {showOnboarding && (
                <OnboardingWizard
                  session={session}
                  state={state}
                  users={users}
                  tenantId={tenant.id}
                  saveRisk={saveRiskLogged}
                  saveTask={saveAdhocTaskLogged}
                  onComplete={handleOnboardingComplete}
                  onStartPart2={() => handleLaunchGuide('employee-lifecycle')}
                />
              )}

              {activeGuide === 'employee-lifecycle' && (
                <EmployeeLifecycleWizard
                  state={state}
                  updateState={updateState}
                  session={session}
                  onClose={handleCloseGuide}
                />
              )}

              <CommandMenu
                state={state}
                session={session}
                pushModal={modals.push}
                allControls={allControls}
              />

              {isBetaTenant && (
                <BetaTestBar
                  tenantId={tenant.id}
                  onResetEnvironment={handleBetaReset}
                  onAnswersChange={handleBetaAnswersChange}
                />
              )}

              {isDevEnvOnly() && <PRTestBar />}
            </div>
          </div>
        </ErrorBoundary>
      </ControlFrameworkPreferenceProvider>
    </BrowserRouter>
  );
}
